import { TokenMetadata } from '../../services/ft-contract';
import { SWAP_MODE } from '../../pages/SwapPage';
import { useEffect, useState } from 'react';
import {
  get_pool,
  PoolInfo,
  V3_POOL_FEE_LIST,
  V3_POOL_SPLITTER,
  getV3PoolId,
} from '../../services/swapV3';
import { useIndexerStatus } from '../pool';
import BigNumber from 'bignumber.js';
import Big from 'big.js';
import {
  scientificNotationToString,
  toReadableNumber,
  getAllocationsLeastOne,
} from '../../utils/numbers';
import _ from 'lodash';

export const useLimitOrder = ({
  tokenIn,
  swapMode,
  tokenOut,
  selectedV3LimitPool,
  setSelectedV3LimitPool,
  loadingTrigger,
  tokenPriceList,
}: {
  tokenIn: TokenMetadata;
  tokenOut: TokenMetadata;
  swapMode: SWAP_MODE;
  selectedV3LimitPool: string;
  setSelectedV3LimitPool?: (pool: string) => void;
  loadingTrigger?: boolean;
  tokenPriceList: Record<string, any>;
}) => {
  const price_x = tokenPriceList?.[tokenIn?.id]?.price;

  const price_y = tokenPriceList?.[tokenOut?.id]?.price;

  const notLimitMode = swapMode !== SWAP_MODE.LIMIT;

  const [quoteDone, setQuoteDone] = useState<boolean>(false);

  const [pools, setPools] = useState<(PoolInfo | null)[]>();

  const [mostPoolDetail, setMostPoolDetail] = useState<PoolInfo>();

  const { fail: indexerFail } = useIndexerStatus();

  const [poolToOrderCounts, setPoolToOrderCounts] = useState<{
    [key: string]: string | null;
  }>();
  const [everyPoolTvl, setEveryPoolTvl] =
    useState<{ [key: string]: string | null }>();

  useEffect(() => {
    if (!selectedV3LimitPool) return;
    setQuoteDone(false);
    get_pool(selectedV3LimitPool, tokenIn.id)
      .then((pool) => {
        if (pool.state === 'Paused') {
          setMostPoolDetail(null);
          return;
        }
        setMostPoolDetail(pool);
      })
      .catch((error) => {
        setMostPoolDetail(null);
      })
      .finally(() => {
        setQuoteDone(true);
      });
  }, [selectedV3LimitPool, loadingTrigger, swapMode]);

  useEffect(() => {
    if (notLimitMode || !tokenIn || !tokenOut) {
      return;
    }
    Promise.all(
      V3_POOL_FEE_LIST.map((fee) =>
        get_pool(getV3PoolId(tokenIn.id, tokenOut.id, fee), tokenIn.id)
      )
    )
      .then((poolsForPair) => {
        setPools(poolsForPair);

        const counts = poolsForPair?.map((pool: PoolInfo) => {
          if (!pool) return 0;
          const tokenX = pool.token_x === tokenIn.id ? tokenIn : tokenOut;
          const tokenY = pool.token_y === tokenOut.id ? tokenOut : tokenIn;

          const priceX = pool.token_x === tokenIn.id ? price_x : price_y;
          const priceY = pool.token_y === tokenOut.id ? price_y : price_x;
          const { total_x, total_y, total_fee_x_charged, total_fee_y_charged } =
            pool;
          const totalX = new BigNumber(total_x)
            .minus(total_fee_x_charged)
            .toFixed();
          const totalY = new BigNumber(total_y)
            .minus(total_fee_y_charged)
            .toFixed();
          const tvlx = new Big(toReadableNumber(tokenX.decimals, totalX))
            .times(priceX || '0')
            .toNumber();

          const tvly = new Big(toReadableNumber(tokenY.decimals, totalY))
            .times(priceY || '0')
            .toNumber();

          return tvlx + tvly;
        });

        const sumOfCounts = _.sum(counts);

        const rawPercents =
          sumOfCounts === 0
            ? ['0', '0', '0', '0']
            : counts.map((count) =>
                scientificNotationToString(
                  ((count / sumOfCounts) * 100).toString()
                )
              );

        const nonZeroIndexes: number[] = [];

        rawPercents.forEach((percent, index) => {
          if (Number(percent) > 0) {
            nonZeroIndexes.push(index);
          }
        });

        const nonZeroPercents = rawPercents.filter(
          (percent) => Number(percent) > 0
        );

        const checkedNonZero = getAllocationsLeastOne(nonZeroPercents);

        const finalPercents = rawPercents.map((percent, index) => {
          if (nonZeroIndexes.includes(index)) {
            const adjustedPercent =
              checkedNonZero[nonZeroIndexes.indexOf(index)];
            return adjustedPercent;
          }
          return percent;
        });

        const percents =
          sumOfCounts === 0 ? ['0', '0', '0', '0'] : finalPercents;

        const percentsNew = percents.map((percent, index) =>
          !!poolsForPair[index] ? percent : null
        );

        const toCounts = percentsNew.reduce(
          (percentsByPoolId, percent, index) => {
            return {
              ...percentsByPoolId,
              [getV3PoolId(tokenIn.id, tokenOut.id, V3_POOL_FEE_LIST[index])]:
                percent,
            };
          },
          {}
        );

        if (
          poolsForPair?.some(
            (pool) =>
              !!pool && (Number(pool?.total_x) > 0 || Number(pool?.total_y) > 0)
          ) &&
          percents.every((percent) => Number(percent) === 0) &&
          !indexerFail
        )
          return;
        const countsByPoolId = {};
        Object.keys(toCounts).forEach((pool_id: string, index) => {
          countsByPoolId[pool_id] = toCounts[pool_id] ? counts[index] : null;
        });
        setEveryPoolTvl(countsByPoolId);
        setPoolToOrderCounts(toCounts);
      })
      .catch((error) => {
        const allPoolsForThisPair = V3_POOL_FEE_LIST.map((fee) =>
          getV3PoolId(tokenIn.id, tokenOut.id, fee)
        );
        setSelectedV3LimitPool(allPoolsForThisPair[2]);
      });
  }, [tokenIn?.id, tokenOut?.id, tokenPriceList, swapMode, indexerFail]);

  useEffect(() => {
    if (!poolToOrderCounts) return;

    const countValues = Object.values(poolToOrderCounts);

    const maxOrderIndex = countValues.findIndex(
      (count) =>
        count !== null &&
        count ===
          _.maxBy(countValues, (candidateCount) =>
            Number(candidateCount == null ? '-1' : candidateCount)
          )
    );
    const allPoolsForThisPair = V3_POOL_FEE_LIST.map((fee) =>
      getV3PoolId(tokenIn.id, tokenOut.id, fee)
    );

    if (countValues.every((count) => count === countValues[0])) {
      setSelectedV3LimitPool(allPoolsForThisPair[2]);
    } else {
      setSelectedV3LimitPool(
        allPoolsForThisPair[maxOrderIndex === -1 ? 2 : maxOrderIndex]
      );
    }
  }, [
    Object.keys(poolToOrderCounts || {}).join('-'),
    Object.values(poolToOrderCounts || {}).join('-'),
    tokenPriceList,
    swapMode,
  ]);

  return {
    poolPercents: notLimitMode ? null : poolToOrderCounts,
    fee: !selectedV3LimitPool
      ? null
      : Number(selectedV3LimitPool.split(V3_POOL_SPLITTER)[2]),
    mostPoolDetail,
    quoteDone,
    idToPools: pools,
    setQuoteDone,
    everyPoolTvl,
  };
};
